/* ============================================================
   RS Boards — storefront client + heart board-picker controller.
   Talks to the Supabase backend through the Shopify App Proxy
   (/apps/boards/*). Identity is verified server-side from the
   signed proxy params; the only thing we send is a guest session
   id so the server can stitch guest -> customer on login.
   ============================================================ */
(function () {
  "use strict";

  if (window.RSBoards) return; // guard against double-load

  var CFG = window.RS_BOARDS_CONFIG || { apiBase: "/apps/boards" };
  var SESSION_KEY = "rs_boards_session";
  var SAVED_KEY = "rs_boards_saved"; // local hint for heart fill state

  /* ---------------- session identity ---------------- */
  function sessionId() {
    var id = "";
    try { id = localStorage.getItem(SESSION_KEY) || ""; } catch (e) {}
    if (!id) {
      id = (window.crypto && crypto.randomUUID && crypto.randomUUID()) ||
        ("s-" + Date.now() + "-" + Math.random().toString(36).slice(2));
      try { localStorage.setItem(SESSION_KEY, id); } catch (e) {}
    }
    try {
      document.cookie = SESSION_KEY + "=" + encodeURIComponent(id) +
        "; path=/; max-age=" + (365 * 24 * 3600) + "; SameSite=Lax";
    } catch (e) {}
    return id;
  }

  /* ---------------- saved-product hint set ---------------- */
  function savedSet() {
    try { return new Set(JSON.parse(localStorage.getItem(SAVED_KEY) || "[]")); }
    catch (e) { return new Set(); }
  }
  function persistSaved(set) {
    try { localStorage.setItem(SAVED_KEY, JSON.stringify(Array.from(set))); } catch (e) {}
  }
  function markSaved(productId, on) {
    var set = savedSet();
    if (on) set.add(String(productId)); else set.delete(String(productId));
    persistSaved(set);
  }
  function isSaved(productId) { return savedSet().has(String(productId)); }

  /* ---------------- persistent SWR cache (localStorage) ----------------
     Lets the boards grid + board detail paint instantly on reload / return
     navigation while a fresh copy revalidates in the background. Entries are
     namespaced per identity (customer id when logged in, else guest session)
     so a different account on the same browser never sees stale data. Only
     price-free JSON is stored here; card pricing is handled separately. */
  var STORE_PREFIX = "rs_boards_cache:";
  function userKey() {
    return (CFG.loggedIn && CFG.customerId) ? ("c" + CFG.customerId) : ("g" + sessionId());
  }
  function storeKey(ns) { return STORE_PREFIX + userKey() + ":" + ns; }
  function cacheGet(ns, maxAgeMs) {
    try {
      var raw = localStorage.getItem(storeKey(ns));
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || typeof o.t !== "number") return null;
      if (maxAgeMs && (Date.now() - o.t > maxAgeMs)) return null;
      return { v: o.v, age: Date.now() - o.t };
    } catch (e) { return null; }
  }
  function cacheSet(ns, value) {
    try { localStorage.setItem(storeKey(ns), JSON.stringify({ t: Date.now(), v: value })); }
    catch (e) { /* quota / private mode — caching is best-effort */ }
  }
  function cacheDel(ns) { try { localStorage.removeItem(storeKey(ns)); } catch (e) {} }

  /* ---------------- API ---------------- */
  function api(path, opts) {
    opts = opts || {};
    var url = new URL(CFG.apiBase + path, location.origin);
    url.searchParams.set("session_id", sessionId());
    return fetch(url.toString(), {
      method: opts.method || "GET",
      headers: opts.body ? { "Content-Type": "application/json" } : undefined,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      credentials: "same-origin"
    }).then(function (res) {
      if (res.status === 204) return null;
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) {
          var err = new Error((data && data.error) || res.statusText);
          err.status = res.status;
          err.code = data && data.code;
          throw err;
        }
        return data;
      });
    });
  }

  var Boards = {
    sessionId: sessionId,
    isSaved: isSaved,
    membership: function (productId) {
      return api("/membership" + (productId ? "?productId=" + encodeURIComponent(productId) : ""));
    },
    list: function () { return api("/boards"); },
    create: function (title, visibility) {
      var body = { title: title };
      if (visibility) body.visibility = visibility;
      return api("/boards", { method: "POST", body: body });
    },
    update: function (id, fields) { return api("/boards/" + id, { method: "PATCH", body: fields }); },
    remove: function (id) { return api("/boards/" + id, { method: "DELETE" }); },
    detail: function (id) { return api("/boards/" + id); },
    addItem: function (id, product) { return api("/boards/" + id + "/items", { method: "POST", body: product }); },
    removeItem: function (id, itemId) { return api("/boards/" + id + "/items/" + itemId, { method: "DELETE" }); },
    reorder: function (id, order) { return api("/boards/" + id + "/items/reorder", { method: "PATCH", body: { order: order } }); },
    save: function (payload) { return api("/save", { method: "POST", body: payload }); },
    share: function (id, visibility, rotate) { return api("/boards/" + id + "/share", { method: "POST", body: { visibility: visibility, rotate: rotate } }); },
    publicBoard: function (token) { return api("/b/" + encodeURIComponent(token) + "?format=json"); },

    /* ---- collaborators ---- */
    identity: function (name, email) { return api("/identity", { method: "POST", body: { name: name, email: email } }); },
    collaborators: function (id) { return api("/boards/" + id + "/collaborators"); },
    createInvite: function (id, role) { return api("/boards/" + id + "/invites", { method: "POST", body: { role: role } }); },
    revokeInvite: function (id, token) { return api("/boards/" + id + "/invites/" + encodeURIComponent(token), { method: "DELETE" }); },
    join: function (token, name, email) { return api("/join", { method: "POST", body: { token: token, name: name, email: email } }); },
    setRole: function (id, memberId, role) { return api("/boards/" + id + "/collaborators/" + memberId, { method: "PATCH", body: { role: role } }); },
    removeCollaborator: function (id, memberId) { return api("/boards/" + id + "/collaborators/" + memberId, { method: "DELETE" }); },
    leave: function (id) { return api("/boards/" + id + "/collaborators/me", { method: "DELETE" }); }
  };

  /* ---------------- identity sync (display name/email for avatars) ---------------- */
  // Logged-in customers register their name/email once per session so they
  // appear correctly in collaborator lists. Authorization never trusts this.
  function syncIdentity() {
    if (!CFG.loggedIn) return;
    if (!CFG.customerName && !CFG.customerEmail) return;
    try {
      var key = "rs_boards_identity_synced";
      if (sessionStorage.getItem(key) === "1") return;
      Boards.identity(CFG.customerName || "", CFG.customerEmail || "")
        .then(function () { try { sessionStorage.setItem(key, "1"); } catch (e) {} })
        .catch(function () {});
    } catch (e) {
      Boards.identity(CFG.customerName || "", CFG.customerEmail || "").catch(function () {});
    }
  }

  /* ---------------- boards list cache (stale-while-revalidate) ---------------- */
  // One shared cache for every picker on the page. The page-load header-count
  // fetch warms it, so opening the heart renders instantly with no round-trip.
  var _boardsCache = null;
  var _boardsFetchedAt = 0;
  var _boardsInflight = null;
  var _boardsDirty = false;
  var BOARDS_TTL = 30000; // ms before an open triggers a background refresh
  var BOARDS_PERSIST_MAX = 24 * 3600 * 1000; // disk cache is shown for up to a day, always revalidated

  // Hydrate the shared cache from localStorage so the grid + header count paint
  // instantly on reload / return navigation. Marked stale so it still revalidates.
  (function hydrateBoardsFromDisk() {
    var hit = cacheGet("boards", BOARDS_PERSIST_MAX);
    if (hit && Array.isArray(hit.v)) {
      _boardsCache = hit.v;
      _boardsFetchedAt = 0; // force a background revalidation on first access
    }
  })();

  function fetchBoardsFresh() {
    if (_boardsInflight) return _boardsInflight;
    _boardsInflight = Boards.list().then(function (b) {
      _boardsCache = b || [];
      _boardsFetchedAt = Date.now();
      _boardsDirty = false;
      _boardsInflight = null;
      cacheSet("boards", _boardsCache);
      return _boardsCache;
    }).catch(function (e) {
      _boardsInflight = null;
      throw e;
    });
    return _boardsInflight;
  }

  function boardsAreStale() {
    return _boardsDirty || (Date.now() - _boardsFetchedAt > BOARDS_TTL);
  }

  // Warm the cache early — but never for brand-new guests, since GET /boards
  // creates a guest owner row server-side. Safe = logged in or returning saver.
  function prefetchBoards() {
    if (_boardsCache || _boardsInflight) return;
    if (!CFG.loggedIn && savedSet().size === 0) return;
    fetchBoardsFresh().catch(function () {});
  }
  Boards.prefetch = prefetchBoards;

  // Shared stale-while-revalidate accessor so the boards page reuses the very
  // same cache as the heart picker (no duplicate /boards round-trips).
  Boards.listSWR = function () {
    var needsFetch = _boardsDirty || !_boardsCache || boardsAreStale();
    return {
      cached: _boardsCache,
      promise: needsFetch ? fetchBoardsFresh() : Promise.resolve(_boardsCache)
    };
  };
  // Let the page invalidate the shared cache after it mutates boards.
  Boards.bustBoards = function () { _boardsDirty = true; };
  Boards.primeBoards = function (list) {
    if (!list) return;
    _boardsCache = list; _boardsFetchedAt = Date.now(); _boardsDirty = false;
    cacheSet("boards", list);
  };
  // Read the shared cache without triggering a fetch.
  Boards.cachedBoards = function () { return _boardsCache; };

  // Persistent SWR cache helpers, shared with the wishlist page (board detail +
  // rendered card HTML). Namespaced per identity; best-effort.
  Boards.cacheGet = cacheGet;
  Boards.cacheSet = cacheSet;
  Boards.cacheDel = cacheDel;

  /* ---------------- product → board membership cache ---------------- */
  // Keyed by productId; lets a re-opened picker render checks instantly while
  // it revalidates in the background.
  var GENERAL_KEY = "__general__";
  var _membershipCache = {};
  function cacheMembership(pid, map) {
    if (!pid) return;
    var copy = {};
    for (var k in map) if (map[k] && map[k] !== "__pending__") copy[k] = map[k];
    _membershipCache[pid] = copy;
  }

  /* ---------------- shared helpers ---------------- */
  function esc(s) {
    var d = document.createElement("div");
    d.textContent = s == null ? "" : String(s);
    return d.innerHTML;
  }

  var _toast;
  function toast(msg) {
    if (!_toast) {
      _toast = document.createElement("div");
      _toast.className = "rsb-toast";
      _toast.setAttribute("role", "status");
      document.body.appendChild(_toast);
    }
    _toast.textContent = msg;
    _toast.classList.add("is-visible");
    clearTimeout(_toast._t);
    _toast._t = setTimeout(function () { _toast.classList.remove("is-visible"); }, 2200);
  }
  Boards.toast = toast;
  Boards.esc = esc;

  /* ---------------- avatar helpers ---------------- */
  function initials(nameOrEmail) {
    var s = (nameOrEmail == null ? "" : String(nameOrEmail)).trim();
    if (!s) return "?";
    if (s.indexOf("@") >= 0 && s.indexOf(" ") < 0) return s.charAt(0).toUpperCase();
    var parts = s.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
  // Deterministic, brand-leaning avatar background from a stable key.
  function avatarColor(key) {
    var palette = ["#2f4a3c", "#5b4a36", "#3a4660", "#6b3f3a", "#43524a", "#5a4d63", "#4a4a4a", "#3c5358"];
    var str = String(key || "");
    var h = 0;
    for (var i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
    return palette[h % palette.length];
  }
  Boards.initials = initials;
  Boards.avatarColor = avatarColor;

  /* ============================================================
     Heart board-picker controller (delegated, works for cards + PDP)
     ============================================================ */
  var openPicker = null;

  // The panel lives inside a product card whose stacking context sits below
  // sibling cards and site widgets (chat bubble, sticky bars). Promote it to the
  // browser top layer via the Popover API so it always paints above everything,
  // regardless of any ancestor's z-index, overflow, or transform. Falls back to
  // a high z-index on the (rare) browser without popover support.
  var SUPPORTS_POPOVER = typeof HTMLElement !== "undefined" &&
    typeof HTMLElement.prototype.showPopover === "function";

  function showPop(pop, root) {
    if (SUPPORTS_POPOVER) {
      if (pop.getAttribute("popover") !== "manual") pop.setAttribute("popover", "manual");
      pop.hidden = false;
      try { pop.showPopover(); } catch (e) { /* already open */ }
    } else {
      pop.hidden = false;
      if (root) root.style.zIndex = "2147483647";
    }
  }

  function hidePop(pop, root) {
    if (SUPPORTS_POPOVER && pop.matches && pop.matches(":popover-open")) {
      try { pop.hidePopover(); } catch (e) {}
    }
    pop.hidden = true;
    if (root) root.style.zIndex = "";
  }

  function clearPopPosition(pop) {
    if (!pop) return;
    pop.style.position = "";
    pop.style.margin = "";
    pop.style.left = "";
    pop.style.right = "";
    pop.style.top = "";
    pop.style.bottom = "";
    pop.style.width = "";
    pop.style.maxWidth = "";
  }

  // The board list panel is wider than a product card, so anchoring it to the
  // heart (right:0) pushes it off-screen on narrow grids. Position it against
  // the viewport instead: a bottom sheet on phones, a clamped dropdown on wider
  // screens. As a top-layer popover its containing block is the viewport, so
  // position:fixed coordinates are correct even under transformed ancestors.
  function positionPicker(root) {
    if (!root) return;
    var pop = root.querySelector(".rs-bp__pop");
    var heart = root.querySelector(".rs-bp__heart");
    if (!pop || !heart || pop.hidden) return;
    clearPopPosition(pop);
    pop.style.margin = "0"; // override the UA popover `margin: auto` centering

    if (window.matchMedia("(max-width: 749px)").matches) {
      pop.style.position = "fixed";
      pop.style.left = "16px";
      pop.style.right = "16px";
      pop.style.bottom = "16px";
      pop.style.top = "auto";
      pop.style.width = "auto";
      pop.style.maxWidth = "none";
      return;
    }

    var hr = heart.getBoundingClientRect();
    var popW = pop.offsetWidth || 300;
    var margin = 12;
    var left = hr.right - popW; // prefer right-aligned to the heart
    left = Math.max(margin, Math.min(left, window.innerWidth - popW - margin));
    pop.style.position = "fixed";
    pop.style.right = "auto";
    pop.style.left = Math.round(left) + "px";
    pop.style.top = Math.round(hr.bottom + 8) + "px";
  }

  function closeOpen() {
    if (!openPicker) return;
    var pop = openPicker.querySelector(".rs-bp__pop");
    var heart = openPicker.querySelector(".rs-bp__heart");
    if (pop) { hidePop(pop, openPicker); clearPopPosition(pop); }
    if (heart) heart.setAttribute("aria-expanded", "false");
    openPicker = null;
  }

  function productData(root) {
    return {
      productId: root.getAttribute("data-product-id"),
      variantId: root.getAttribute("data-variant-id") || null,
      handle: root.getAttribute("data-handle"),
      title: root.getAttribute("data-title"),
      imageUrl: root.getAttribute("data-image") || null
    };
  }

  var BP_HEART = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 20s-7-4.4-7-9.2A3.8 3.8 0 0112 7a3.8 3.8 0 017 3.8C19 15.6 12 20 12 20z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';
  var BP_CHECK = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12.5l4.2 4.2L19 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function bpThumb(src) {
    if (!src) return "";
    return src.indexOf("width=") >= 0 ? src : src + (src.indexOf("?") >= 0 ? "&" : "?") + "width=160";
  }

  function bpRow(o) {
    var thumb = o.img
      ? '<span class="rs-bp__row-thumb"><img src="' + esc(o.img) + '" alt="" loading="lazy"></span>'
      : '<span class="rs-bp__row-thumb rs-bp__row-thumb--ph' + (o.general ? " rs-bp__row-thumb--gen" : "") + '">' +
        (o.general ? BP_HEART : "") + "</span>";
    var attr = o.general
      ? ' data-rs-bp-general="1"' + (o.id ? ' data-board-id="' + esc(o.id) + '"' : "")
      : ' data-board-id="' + esc(o.id) + '"';
    if (o.itemId) attr += ' data-item-id="' + esc(o.itemId) + '"';
    var ind = o.checked
      ? '<span class="rs-bp__check" aria-hidden="true">' + BP_CHECK + "</span>"
      : '<span class="rs-bp__radio" aria-hidden="true"></span>';
    return '<li><button type="button" class="rs-bp__choice' + (o.checked ? " is-checked" : "") + '"' +
      attr + ' aria-pressed="' + (o.checked ? "true" : "false") + '">' +
      thumb +
      '<span class="rs-bp__choice-text">' +
        '<span class="rs-bp__choice-name">' + esc(o.title) + "</span>" +
        (o.sub ? '<span class="rs-bp__choice-sub">' + esc(o.sub) + "</span>" : "") +
      "</span>" +
      ind +
      "</button></li>";
  }

  function renderBoardList(root, boards) {
    root._bpBoards = boards || [];
    paintBoardList(root, "");
  }

  function paintBoardList(root, q) {
    var list = root.querySelector("[data-rs-bp-list]");
    if (!list) return;
    var boards = root._bpBoards || [];
    var membership = root._bpMembership || {};
    q = (q || "").trim().toLowerCase();

    // The general "My Saves" board is surfaced as the pinned Wishlist row.
    var def = null, rest = [];
    boards.forEach(function (b) {
      if (!def && b.title === "My Saves") def = b; else rest.push(b);
    });

    var rows = "";
    if (!q || "wishlist".indexOf(q) >= 0) {
      var genChecked = def ? !!membership[def.id] : !!membership[GENERAL_KEY];
      var genItem = def ? membership[def.id] : membership[GENERAL_KEY];
      rows += bpRow({
        general: true,
        id: def ? def.id : "",
        title: "Wishlist",
        sub: "Default \u00b7 all saved items",
        img: (def && def.coverImages && def.coverImages[0]) ? bpThumb(def.coverImages[0]) : "",
        checked: genChecked,
        itemId: genItem
      });
    }
    rest.filter(function (b) {
      return !q || (b.title || "").toLowerCase().indexOf(q) >= 0;
    }).forEach(function (b) {
      var n = b.itemCount || 0;
      rows += bpRow({
        id: b.id,
        title: b.title,
        sub: n + (n === 1 ? " item" : " items"),
        img: (b.coverImages && b.coverImages[0]) ? bpThumb(b.coverImages[0]) : "",
        checked: !!membership[b.id],
        itemId: membership[b.id]
      });
    });

    list.innerHTML = rows || '<li class="rs-bp__empty">No boards match.</li>';
  }

  function loadBoards(root) {
    // Reset the create-board form + search each time the picker opens.
    var form = root.querySelector("[data-rs-bp-new]");
    if (form) form.hidden = true;
    var ct = root.querySelector("[data-rs-bp-createtoggle]");
    if (ct) ct.style.display = "";
    var search = root.querySelector("[data-rs-bp-search]");
    if (search) search.value = "";

    var list = root.querySelector("[data-rs-bp-list]");

    // Cache hit → render instantly, then revalidate in the background if stale.
    if (_boardsCache) {
      renderBoardList(root, _boardsCache);
      if (boardsAreStale()) {
        fetchBoardsFresh().then(function (boards) {
          if (openPicker === root) {
            root._bpBoards = boards;
            var s = root.querySelector("[data-rs-bp-search]");
            paintBoardList(root, s ? s.value : "");
          }
        }).catch(function () {});
      }
      return;
    }

    // Cold start → show loading and fetch once (cache fills for next time).
    if (list) list.innerHTML = '<li class="rs-bp__loading">Loading…</li>';
    fetchBoardsFresh().then(function (boards) {
      renderBoardList(root, boards);
    }).catch(function () {
      if (list) list.innerHTML = '<li class="rs-bp__empty">Could not load boards.</li>';
    });
  }

  // Fetch which of the owner's boards already contain this product so the
  // rows can render their checked state. Skipped for brand-new guests to
  // avoid creating an owner row server-side before they've saved anything.
  function loadMembership(root) {
    var pid = root.getAttribute("data-product-id");
    if (!pid) return;
    if (!CFG.loggedIn && savedSet().size === 0) { root._bpMembership = {}; return; }

    // Cache hit → render checks instantly, then revalidate quietly.
    if (_membershipCache[pid]) {
      root._bpMembership = Object.assign({}, _membershipCache[pid]);
      if (openPicker === root) {
        var sc = root.querySelector("[data-rs-bp-search]");
        paintBoardList(root, sc ? sc.value : "");
      }
    }

    Boards.membership(pid).then(function (res) {
      var map = {};
      ((res && res.boards) || []).forEach(function (m) { map[m.boardId] = m.itemId; });
      // Preserve any in-flight optimistic entries that the server hasn't caught up to.
      var cur = root._bpMembership || {};
      for (var k in cur) if (cur[k] === "__pending__" && !map[k]) map[k] = "__pending__";
      root._bpMembership = map;
      cacheMembership(pid, map);
      if (openPicker === root) {
        var s = root.querySelector("[data-rs-bp-search]");
        paintBoardList(root, s ? s.value : "");
      }
    }).catch(function () {});
  }

  function afterMembershipChange(root) {
    var s = root.querySelector("[data-rs-bp-search]");
    paintBoardList(root, s ? s.value : "");
    var any = Object.keys(root._bpMembership || {}).length > 0;
    markSaved(root.getAttribute("data-product-id"), any);
    setHeart(root, any);
    positionPicker(root);
  }

  function setHeart(root, on) {
    var heart = root.querySelector(".rs-bp__heart");
    if (heart) heart.classList.toggle("is-saved", !!on);
    // Mirror across any other picker for the same product on the page.
    var pid = root.getAttribute("data-product-id");
    document.querySelectorAll('[data-rs-board-picker][data-product-id="' + pid + '"] .rs-bp__heart')
      .forEach(function (h) { h.classList.toggle("is-saved", !!on); });
  }

  // Reconcile board counts/covers + header badge in the background, off the
  // critical path so the UI never waits on a /boards round-trip.
  function bgRefreshBoards(root) {
    fetchBoardsFresh().then(function (boards) {
      if (openPicker === root) {
        root._bpBoards = boards;
        var s = root.querySelector("[data-rs-bp-search]");
        paintBoardList(root, s ? s.value : "");
      }
      updateHeaderCount();
    }).catch(function () {});
  }

  // Toggle a product in/out of a board from a picker row. Everything is
  // optimistic: the check + heart flip instantly, the network runs in the
  // background, and we revert + toast only if the server rejects it.
  function toggleChoice(root, choice) {
    if (choice.classList.contains("is-busy")) return;
    var bid = choice.getAttribute("data-board-id");
    var itemId = choice.getAttribute("data-item-id");
    var pid = root.getAttribute("data-product-id");
    var map = root._bpMembership || (root._bpMembership = {});

    // REMOVE — we hold a real item id.
    if (bid && itemId && itemId !== "__pending__") {
      delete map[bid];
      _boardsDirty = true;
      afterMembershipChange(root);            // instant uncheck + heart update
      Boards.removeItem(bid, itemId).then(function () {
        cacheMembership(pid, map);
        bgRefreshBoards(root);
      }).catch(function (err) {
        map[bid] = itemId;                    // revert
        if (openPicker === root) afterMembershipChange(root);
        toast((err && err.message) || "Could not remove");
      });
      return;
    }

    // ADD — to an existing board (bid) or the general Wishlist (no bid).
    var key = bid || GENERAL_KEY;
    map[key] = "__pending__";
    _boardsDirty = true;
    afterMembershipChange(root);              // instant check + heart update
    choice.classList.add("is-busy");
    var payload = bid ? { boardId: bid } : { newTitle: "My Saves" };
    saveToBoard(root, payload, key).then(function () {
      choice.classList.remove("is-busy");
    });
  }

  // Persist a save. If optimisticKey is provided the membership entry already
  // exists as "__pending__"; we just swap in the real id once it returns.
  function saveToBoard(root, payload, optimisticKey) {
    var pid = root.getAttribute("data-product-id");
    var data = productData(root);
    for (var k in data) if (data[k] != null && payload[k] === undefined) payload[k] = data[k];
    var map = root._bpMembership || (root._bpMembership = {});

    // Optimistic heart even before the request resolves.
    markSaved(pid, true);
    setHeart(root, true);

    return Boards.save(payload).then(function (res) {
      var bId = res && res.board && res.board.id;
      var iId = res && res.item && res.item.id;
      if (optimisticKey && optimisticKey !== bId) delete map[optimisticKey];
      if (bId) map[bId] = iId || "__pending__";
      // Surface the saved board locally so its row renders checked instantly —
      // covers the first-ever save to the general Wishlist and brand-new boards.
      if (res && res.board && Array.isArray(root._bpBoards)) {
        var exists = root._bpBoards.some(function (b) { return b.id === bId; });
        if (!exists) root._bpBoards.unshift(res.board);
      }
      cacheMembership(pid, map);
      _boardsDirty = true;
      toast(res && res.duplicated ? "Already saved" : "Saved to " + ((res.board && res.board.title) || "board"));
      if (openPicker === root) afterMembershipChange(root);
      bgRefreshBoards(root);
    }).catch(function (err) {
      if (optimisticKey) delete map[optimisticKey];
      var any = Object.keys(map).length > 0;
      markSaved(pid, any); setHeart(root, any);
      if (openPicker === root) afterMembershipChange(root);
      toast((err && err.message) || "Could not save");
    });
  }

  // Hydrate heart fill state on load from the local saved hint.
  function hydrateHearts() {
    document.querySelectorAll("[data-rs-board-picker]").forEach(function (root) {
      if (isSaved(root.getAttribute("data-product-id"))) setHeart(root, true);
    });
  }

  /* ---------------- reconcile stale heart hints ---------------- */
  // The local saved hint is just a fast-paint cache; it can go stale when an
  // item is removed on the boards page (or another device), leaving a filled
  // heart for a product that's no longer saved. We re-check those against the
  // server and clear the ones that are truly gone. Only products the hint marks
  // saved are checked, so this stays cheap (usually 0-1 calls on a PDP).
  var _reconciled = {};
  function reconcileProduct(pid, force) {
    pid = String(pid || "");
    if (!pid) return;
    if (!force && _reconciled[pid]) return;
    _reconciled[pid] = true;
    Boards.membership(pid).then(function (res) {
      var boards = (res && res.boards) || [];
      if (boards.length) {
        var map = {};
        boards.forEach(function (m) { map[m.boardId] = m.itemId; });
        _membershipCache[pid] = map;
        markSaved(pid, true);
      } else {
        delete _membershipCache[pid];
        markSaved(pid, false);
        document.querySelectorAll('[data-rs-board-picker][data-product-id="' + pid + '"] .rs-bp__heart')
          .forEach(function (h) { h.classList.remove("is-saved"); });
        updateHeaderCount();
      }
    }).catch(function () {});
  }
  Boards.reconcileProduct = reconcileProduct;

  function reconcileSavedHearts() {
    if (!CFG.loggedIn) return; // guests' hint is only ever mutated locally via the picker
    var saved = savedSet();
    if (!saved.size) return;
    var seen = {};
    document.querySelectorAll("[data-rs-board-picker]").forEach(function (root) {
      var pid = root.getAttribute("data-product-id");
      if (!pid || seen[pid]) return;
      seen[pid] = true;
      if (saved.has(String(pid))) reconcileProduct(pid);
    });
  }
  Boards.reconcileHearts = reconcileSavedHearts;

  document.addEventListener("click", function (e) {
    var heart = e.target.closest("[data-rs-board-picker] .rs-bp__heart");
    if (heart) {
      e.preventDefault();
      var root = heart.closest("[data-rs-board-picker]");
      var pop = root.querySelector(".rs-bp__pop");
      var wasOpen = openPicker === root;
      closeOpen();
      if (!wasOpen && pop) {
        openPicker = root;
        showPop(pop, root);
        heart.setAttribute("aria-expanded", "true");
        positionPicker(root);
        loadBoards(root);
        loadMembership(root);
      }
      return;
    }

    // Close (X) button.
    var closeBtn = e.target.closest("[data-rs-bp-close]");
    if (closeBtn) { e.preventDefault(); closeOpen(); return; }

    // Board row → toggle the product in/out of that board (popover stays open).
    var choice = e.target.closest("[data-rs-bp-list] .rs-bp__choice");
    if (choice) {
      e.preventDefault();
      if (choice.classList.contains("is-busy")) return;
      var rootC2 = choice.closest("[data-rs-board-picker]");
      toggleChoice(rootC2, choice);
      return;
    }

    // "Create board" footer reveals the inline name field.
    var ctoggle = e.target.closest("[data-rs-bp-createtoggle]");
    if (ctoggle) {
      e.preventDefault();
      var rootC = ctoggle.closest("[data-rs-board-picker]");
      var formC = rootC && rootC.querySelector("[data-rs-bp-new]");
      if (formC) {
        formC.hidden = false;
        ctoggle.style.display = "none";
        var inpC = formC.querySelector("[data-rs-bp-input]");
        if (inpC) inpC.focus();
      }
      return;
    }

    // Outside click closes.
    if (openPicker && !e.target.closest(".rs-bp__pop") && !e.target.closest(".rs-bp__heart")) {
      closeOpen();
    }
  });

  document.addEventListener("submit", function (e) {
    var form = e.target.closest("[data-rs-bp-new]");
    if (!form) return;
    e.preventDefault();
    var root = form.closest("[data-rs-board-picker]");
    var input = form.querySelector("[data-rs-bp-input]");
    var title = input && input.value.trim();
    if (!title) return;
    // Reset the create UI immediately; the save runs in the background and the
    // new (checked) board row arrives via the background refresh.
    if (input) input.value = "";
    form.hidden = true;
    var ct = root.querySelector("[data-rs-bp-createtoggle]");
    if (ct) ct.style.display = "";
    saveToBoard(root, { newTitle: title });
  });

  document.addEventListener("input", function (e) {
    var search = e.target.closest("[data-rs-bp-search]");
    if (!search) return;
    var root = search.closest("[data-rs-board-picker]");
    if (root) paintBoardList(root, search.value);
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && openPicker) closeOpen();
  });

  // Keep the fixed-positioned panel glued to its heart as the page moves.
  var _bpReflow = null;
  function reflowPicker() {
    if (!openPicker) return;
    if (_bpReflow) return;
    _bpReflow = requestAnimationFrame(function () {
      _bpReflow = null;
      positionPicker(openPicker);
    });
  }
  window.addEventListener("scroll", reflowPicker, { passive: true });
  window.addEventListener("resize", reflowPicker);

  // Warm the boards cache the moment a heart is hovered/focused/touched, so the
  // list is ready before the click lands. prefetchBoards() is self-guarded.
  function maybePrefetchFromHeart(e) {
    var t = e.target;
    if (t && t.closest && t.closest("[data-rs-board-picker] .rs-bp__heart")) prefetchBoards();
  }
  document.addEventListener("pointerover", maybePrefetchFromHeart, { passive: true });
  document.addEventListener("focusin", maybePrefetchFromHeart);
  document.addEventListener("touchstart", maybePrefetchFromHeart, { passive: true });

  /* ---------------- header saved-count badge ---------------- */
  function updateHeaderCount() {
    var badges = document.querySelectorAll(".wishlist-total-count");
    if (!badges.length) return;
    // Avoid creating guest owner rows for visitors who have never saved.
    if (!CFG.loggedIn && savedSet().size === 0) return;
    // Reuse the shared cache fetch so the picker opens instantly afterwards.
    fetchBoardsFresh().then(function (boards) {
      var total = (boards || []).reduce(function (n, b) { return n + (b.itemCount || 0); }, 0);
      badges.forEach(function (el) {
        el.textContent = total;
        el.setAttribute("data-total", String(total));
      });
    }).catch(function () {});
  }

  /* ---------------- boot ---------------- */
  // Claim/merge guest boards on every logged-in page load (idempotent).
  if (CFG.loggedIn) Boards.membership().then(updateHeaderCount).catch(function () {});
  else updateHeaderCount();

  // Register display name/email for collaborator avatars (logged-in only).
  syncIdentity();

  window.RSBoards = Boards;

  function hydrateAndReconcile() {
    hydrateHearts();
    reconcileSavedHearts();
    // Second pass catches collection cards that render after first paint.
    setTimeout(reconcileSavedHearts, 1500);
  }
  if (document.readyState !== "loading") hydrateAndReconcile();
  else document.addEventListener("DOMContentLoaded", hydrateAndReconcile);

  document.dispatchEvent(new CustomEvent("rs-boards:ready"));
})();
